import React, { useCallback, useEffect, useRef, useState } from 'react';
import { m, useReducedMotion } from 'framer-motion';
import BrandMotion from './BrandMotion';
import KoMoldLogo from './KoMoldLogo';
import { useAppState } from '../../context/AppStateContext';

/**
 * The app's 1.5s intro: the mold assembles itself, the name arrives, and the
 * overlay clears.
 *
 * The handover is the part worth explaining. The brief asks for the logo to
 * survive into the login page rather than blink out with the splash, so at
 * 1.25s we measure wherever the page underneath is already rendering its own
 * mark (`data-komold-anchor`) and fly this one onto it while the overlay fades.
 * The two coincide by the time the overlay is gone, so there is no seam — and
 * on a screen with no anchor (a logged-in reload) the logo simply fades with
 * everything else. Measuring beats hand-matching two layouts: it keeps working
 * when either page's spacing changes.
 */

const HANDOVER_MS = 1250;
const TOTAL_MS = 1500;

interface Flight {
    x: number;
    y: number;
    scale: number;
}

interface SplashScreenProps {
    onDone: () => void;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onDone }) => {
    const { appName } = useAppState();
    const reduceMotion = useReducedMotion();
    const logoRef = useRef<HTMLDivElement>(null);
    const [flight, setFlight] = useState<Flight | null>(null);
    const [clearing, setClearing] = useState(false);

    // onDone usually flips state in App; keep it out of the effect's deps so a
    // re-created callback cannot restart the timers mid-intro.
    const done = useRef(onDone);
    done.current = onDone;

    const handOver = useCallback(() => {
        setClearing(true);
        const anchor = document.querySelector('[data-komold-anchor]');
        const from = logoRef.current?.getBoundingClientRect();
        if (!anchor || !from || from.width === 0) return;

        const to = anchor.getBoundingClientRect();
        if (to.width === 0) return;
        setFlight({
            x: to.left + to.width / 2 - (from.left + from.width / 2),
            y: to.top + to.height / 2 - (from.top + from.height / 2),
            scale: to.width / from.width,
        });
    }, []);

    useEffect(() => {
        if (reduceMotion) {
            done.current();
            return;
        }
        const handover = window.setTimeout(handOver, HANDOVER_MS);
        const finish = window.setTimeout(() => done.current(), TOTAL_MS);
        return () => {
            window.clearTimeout(handover);
            window.clearTimeout(finish);
        };
    }, [handOver, reduceMotion]);

    if (reduceMotion) return null;

    return (
        <BrandMotion>
            <m.div
                // Never swallow a click: the login form underneath is live the moment
                // the overlay starts clearing.
                data-testid="brand-splash"
                className="pointer-events-none fixed inset-0 z-[100] flex flex-col items-center justify-center"
                initial={{ opacity: 1 }}
                animate={{ opacity: clearing ? 0 : 1 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
            >
                {/* The ground and its drafting grid fade out; the logo does not. */}
                <div aria-hidden="true" className="absolute inset-0 bg-[#FAFBFC]" />
                <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.55]"
                    style={{
                        backgroundImage:
                            'linear-gradient(to right, #E7EAF0 1px, transparent 1px), linear-gradient(to bottom, #E7EAF0 1px, transparent 1px)',
                        backgroundSize: '64px 64px',
                        maskImage: 'radial-gradient(ellipse 85% 70% at 50% 45%, #000 40%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 85% 70% at 50% 45%, #000 40%, transparent 100%)',
                    }}
                />

                <m.div
                    ref={logoRef}
                    className="relative"
                    animate={{ x: flight?.x ?? 0, y: flight?.y ?? 0, scale: flight?.scale ?? 1 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                >
                    <KoMoldLogo state="assembled" className="h-[88px] w-[88px]" />
                </m.div>

                <m.div
                    className="relative mt-5 text-center"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: clearing ? 0 : 1, y: clearing ? 6 : 0 }}
                    transition={{ duration: clearing ? 0.12 : 0.3, delay: clearing ? 0 : 0.95, ease: 'easeOut' }}
                >
                    <p className="text-[19px] font-semibold tracking-[-0.02em] text-[#0F1317]">KoMold</p>
                    <p className="mt-1 text-[12.5px] text-[#6B7280]">{appName} · ระบบจัดการแม่พิมพ์</p>
                </m.div>
            </m.div>
        </BrandMotion>
    );
};

export default SplashScreen;
